import { useCallback } from 'react'
import { Alert, Button, Modal, Text } from '@/componentsV1/BaseComponents'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import {
  REOPEN_RECONCILIATION_KEY,
  reopenReconciliation,
} from '@/actions/admin/accountReconciliationActions'

export interface ReopenReconciliationModalProps {
  reconciliationId: number
  open: boolean
  close: () => void
  onReopened?: () => void
}

const ReopenReconciliationModal = ({
  reconciliationId,
  open,
  close,
  onReopened,
}: ReopenReconciliationModalProps) => {
  const dispatch = useAppDispatch()
  const isFetching = useReselector(getIsFetching, REOPEN_RECONCILIATION_KEY)
  const error = useReselector(getFetchError, REOPEN_RECONCILIATION_KEY)

  const onConfirm = useCallback(async () => {
    const reopened = await reopenReconciliation(reconciliationId)(dispatch)
    if (reopened) {
      close()
      onReopened?.()
    }
  }, [dispatch, reconciliationId, close, onReopened])

  return (
    <Modal open={open} onClose={close} size="small">
      <Modal.Header as="h3">Reopen this reconciliation?</Modal.Header>
      <Modal.Content>
        {error ? (
          <Alert type="error" title="Couldn't reopen this reconciliation">
            {error.message}
          </Alert>
        ) : (
          <Text>
            This period will no longer be reconciled, and its transactions
            become editable again. Reconcile it against the bank statement once
            the correction is made.
          </Text>
        )}
      </Modal.Content>
      <Modal.Actions>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            width: '100%',
            gap: 8,
          }}
        >
          <Button variant="secondary" onClick={close} disabled={isFetching}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            loading={isFetching}
            disabled={isFetching}
          >
            Reopen
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  )
}

export default ReopenReconciliationModal
